import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { and, count, eq, isNull } from "drizzle-orm";
import { Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@bso/shared/solid";
import { bible_note, bible_verse_highlight } from "@bso/powersync";
import { cn, queryKeys } from "@bso/shared";
import { ArrowRight, BarChart3 } from "@fleet/icons";
import { COMPACT_THRESHOLD, formatCompactNumber } from "@fleet/utils";

interface StatsSectionProps {
  userId: string;
}

interface DBCountResult {
  count: number;
}

export function StatsSection(props: StatsSectionProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const highlights = createPowerSyncQuery<DBCountResult>({
    queryKey: () => queryKeys.bible.highlightCount(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance || !props.userId) {
        return null;
      }
      return dbInstance
        .select({ count: count() })
        .from(bible_verse_highlight)
        .where(
          and(
            eq(bible_verse_highlight.user_id, props.userId),
            isNull(bible_verse_highlight.deleted_at),
          ),
        );
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const notes = createPowerSyncQuery<DBCountResult>({
    queryKey: () => queryKeys.bible.noteCount(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance || !props.userId) {
        return null;
      }
      return dbInstance
        .select({ count: count() })
        .from(bible_note)
        .where(and(eq(bible_note.user_id, props.userId), isNull(bible_note.deleted_at)));
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const highlightsCount = () => highlights.data?.[0]?.count ?? 0;
  const notesCount = () => notes.data?.[0]?.count ?? 0;

  return (
    <Card>
      <CardHeader>
        <div class="flex items-center gap-2">
          <BarChart3 class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Estadísticas de Estudio`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-6">
        <div class="grid grid-cols-2 gap-4">
          <StatCard
            label={t(msg`Destacados`)}
            value={highlightsCount()}
            colorClass="text-warning"
            bgClass="bg-warning/10"
            isLoading={highlights.isPending}
          />
          <StatCard
            label={t(msg`Notas Personales`)}
            value={notesCount()}
            colorClass="text-primary"
            bgClass="bg-primary/10"
            isLoading={notes.isPending}
          />
        </div>
        <Link
          to="/analytics"
          class="flex items-center justify-center gap-1.5 rounded-xl border border-border/40 bg-muted/20 px-4 py-2.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
        >
          {t(msg`View full progress`)}
          <ArrowRight class="h-3.5 w-3.5" />
        </Link>
      </CardContent>
    </Card>
  );
}

function StatCard(props: {
  label: string;
  value: number;
  colorClass: string;
  bgClass: string;
  isLoading?: boolean;
}) {
  const { locale } = useI18n();
  const isCompact = () => props.value >= COMPACT_THRESHOLD;
  const formattedValue = () => formatCompactNumber(props.value, locale());

  return (
    <div
      class={cn(
        "flex flex-col items-center justify-center rounded-2xl p-4 text-center transition-all hover:scale-[1.02]",
        props.bgClass,
      )}
    >
      <Show
        when={props.isLoading}
        fallback={
          <span
            class={cn(
              "text-2xl font-black leading-none tracking-tight select-none",
              props.colorClass,
            )}
          >
            {isCompact() ? formattedValue() : props.value}
          </span>
        }
      >
        <Skeleton class="h-7 w-8" />
      </Show>
      <div class="mt-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-80 select-none">
        {props.label}
      </div>
    </div>
  );
}
